/** Uma folha (Excel ou CSV) transformada em operações sobre o IFC, e o
 *  caminho inverso: o modelo de preenchimento, uma folha com os
 *  elementos e os valores actuais, para a pessoa preencher e voltar a
 *  importar.
 *
 *  Formato da folha (ideia do liteAECO, Inject Properties, Apache-2.0):
 *  - uma coluna chave, que identifica o elemento: por omissão a
 *    GlobalId; pode ser uma propriedade (ex.: "Pset_X.Codigo"), desde
 *    que o valor identifique um só elemento;
 *  - uma coluna por propriedade, com o cabeçalho "Pset.Propriedade",
 *    opcionalmente com o tipo: "Pset.Propriedade [IfcLengthMeasure]";
 *  - as colunas sem ponto no nome (Classe, Nome) são só informativas
 *    e não se importam;
 *  - células vazias não mexem em nada. */

import type { Celula, Folha } from '@core/lib/folhas'
import type { ModeloStep } from '@viewer/lib/step/modeloStep'
import { lerArgTexto, lerListaDeRefs, lerRef } from '@viewer/lib/step/textoStep'
import { canonicalIfcName } from '@modules/boq/lib/ifcCanonical'
import { criarLeitorDeUnidades } from '@viewer/lib/inventario/inventarioPsets'
import { criarLeitor, generoDoTipo, valorLegivel, type FormaDaPropriedade, type Operacao } from './operacoesEmMassa'
import { classificacoesDe, construirIndiceDeAssociacoes, materialDe } from './associacoes'

export interface CabecalhoDePropriedade {
  pset: string
  propriedade: string
  tipoDeValor?: string
  /** Forma de uma propriedade nova: [lista ...], [intervalo ...] ou uma
   *  classe de quantidade ([IfcQuantityArea]). */
  forma?: FormaDaPropriedade
  classeDaQuantidade?: string
}

/** "Pset_X.Nome [IfcLabel]" -> { pset, propriedade, tipoDeValor }. O
 *  corte é no PRIMEIRO ponto: nomes de Pset não levam pontos, nomes de
 *  propriedade às vezes levam. Entre parênteses rectos, opcionalmente a
 *  forma ("lista", "intervalo") e o tipo; uma classe IfcQuantity* diz
 *  que é uma quantidade. */
export function lerCabecalho(h: string): CabecalhoDePropriedade | null {
  // No fim pode vir a unidade entre chavetas ("{mm #11}"), que o modelo
  // de preenchimento escreve para se ler; não se importa.
  const m = /^\s*([^.[\]]+?)\s*\.\s*(.+?)\s*(?:\[\s*(lista|intervalo)?\s*([A-Za-z][A-Za-z0-9_]*)?\s*\])?\s*(?:\{[^}]*\})?\s*$/i.exec(h)
  if (!m) return null
  const out: CabecalhoDePropriedade = { pset: m[1], propriedade: m[2] }
  const forma = m[3]?.toLowerCase()
  const tipo = m[4]
  if (tipo && /^ifcquantity/i.test(tipo)) {
    out.forma = 'quantidade'
    out.classeDaQuantidade = tipo
  } else if (tipo) {
    out.tipoDeValor = tipo
  }
  if (forma === 'lista' || forma === 'intervalo') out.forma = forma
  return out
}

export type ColunaEspecial = { tipo: 'material' } | { tipo: 'classificacao'; sistema: string }

/** "Material" e "Classificação: Uniclass 2015" (também sem acento, ou em
 *  inglês). */
export function lerColunaEspecial(h: string): ColunaEspecial | null {
  const t = h.trim()
  if (/^(material|materiais)$/i.test(t)) return { tipo: 'material' }
  const c = /^(classifica[çc][ãa]o|classification)\s*:\s*(.+)$/i.exec(t)
  if (c) return { tipo: 'classificacao', sistema: c[2].trim() }
  return null
}

const NOMES_DE_GUID = ['globalid', 'guid', 'ifcguid', 'global id']

export interface Chave {
  coluna: number
  /** 'guid', ou a propriedade usada como chave. */
  tipo: 'guid' | 'propriedade'
  propriedade?: CabecalhoDePropriedade
}

export interface ColunaImportada extends CabecalhoDePropriedade {
  coluna: number
  cabecalho: string
}

export type ColunaDeAssociacao = ColunaEspecial & { coluna: number; cabecalho: string }

export interface PlanoDeImportacao {
  chave: Chave | null
  colunas: ColunaImportada[]
  /** Colunas de material e de classificação. */
  associacoes: ColunaDeAssociacao[]
  informativas: string[]
  operacoes: Operacao[]
  /** Linhas sem elemento correspondente (chave em falta, inexistente ou
   *  repetida no modelo). O número é o da folha (1 = cabeçalho). */
  semElemento: { linha: number; chave: string; motivo: string }[]
  /** GUIDs que aparecem em mais de uma linha: a última ganha. */
  repetidas: string[]
  /** Quantas células com valor diferem do que o ficheiro já tem. */
  alteracoes: number
  /** Primeiras alterações, para a pré-visualização. */
  amostra: { guid: string; rotulo: string; antes: string | null; depois: string }[]
}

/** Colunas candidatas a chave: a GlobalId e as de propriedades. */
export function candidatasAChave(cabecalho: string[]): Chave[] {
  const out: Chave[] = []
  cabecalho.forEach((h, i) => {
    if (NOMES_DE_GUID.includes(h.trim().toLowerCase())) out.push({ coluna: i, tipo: 'guid' })
  })
  cabecalho.forEach((h, i) => {
    const p = lerCabecalho(h)
    if (p) out.push({ coluna: i, tipo: 'propriedade', propriedade: p })
  })
  return out
}

/** Valor da propriedade -> GlobalIds dos elementos que o têm. */
function indicePorValor(m: ModeloStep, prop: CabecalhoDePropriedade): Map<string, string[]> {
  const out = new Map<string, string[]>()
  for (const rel of m.idsDaClasse('IFCRELDEFINESBYPROPERTIES')) {
    const a = m.args(rel)
    const c = lerRef(a[5])
    if (c === null || m.classe(c) !== 'IFCPROPERTYSET') continue
    if (lerArgTexto(m.args(c)[2]) !== prop.pset) continue
    for (const p of lerListaDeRefs(m.args(c)[4])) {
      if (m.classe(p) !== 'IFCPROPERTYSINGLEVALUE' || lerArgTexto(m.args(p)[0]) !== prop.propriedade) continue
      const v = valorLegivel(m.args(p)[2])
      if (v === null) continue
      for (const o of lerListaDeRefs(a[4])) {
        const g = lerArgTexto(m.args(o)[0])
        if (g) out.set(v, [...(out.get(v) ?? []), g])
      }
    }
  }
  return out
}

export function planearImportacao(
  linhas: string[][],
  m: ModeloStep,
  escolha?: { chave?: number },
): PlanoDeImportacao {
  const cabecalho = (linhas[0] ?? []).map((h) => (h ?? '').trim())
  const candidatas = candidatasAChave(cabecalho)
  const chave =
    (escolha?.chave !== undefined ? candidatas.find((c) => c.coluna === escolha.chave) : undefined) ??
    candidatas.find((c) => c.tipo === 'guid') ??
    null
  const colunas: ColunaImportada[] = []
  const associacoes: ColunaDeAssociacao[] = []
  const informativas: string[] = []
  cabecalho.forEach((h, i) => {
    if (h === '' || (chave && i === chave.coluna)) return
    const e = lerColunaEspecial(h)
    if (e) {
      associacoes.push({ ...e, coluna: i, cabecalho: h })
      return
    }
    const p = lerCabecalho(h)
    if (p) colunas.push({ ...p, coluna: i, cabecalho: h })
    else informativas.push(h)
  })
  const plano: PlanoDeImportacao = {
    chave,
    colunas,
    associacoes,
    informativas,
    operacoes: [],
    semElemento: [],
    repetidas: [],
    alteracoes: 0,
    amostra: [],
  }
  if (!chave) return plano

  const porValor = chave.tipo === 'propriedade' ? indicePorValor(m, chave.propriedade!) : null
  const ler = criarLeitor(m)
  const ix = associacoes.length > 0 ? construirIndiceDeAssociacoes(m) : null
  const vistos = new Set<string>()
  const registar = (guid: string, rotulo: string, antes: string | null, depois: string) => {
    if (antes === depois) return
    plano.alteracoes++
    if (plano.amostra.length < 50) plano.amostra.push({ guid, rotulo, antes, depois })
  }
  for (let r = 1; r < linhas.length; r++) {
    const linha = linhas[r] ?? []
    if (linha.every((c) => (c ?? '').trim() === '')) continue
    const valorChave = (linha[chave.coluna] ?? '').trim()
    if (!valorChave) {
      plano.semElemento.push({ linha: r + 1, chave: '', motivo: 'Sem valor na coluna chave.' })
      continue
    }
    let guid: string
    if (porValor) {
      const gs = porValor.get(valorChave) ?? []
      if (gs.length !== 1) {
        plano.semElemento.push({
          linha: r + 1,
          chave: valorChave,
          motivo: gs.length === 0 ? 'Nenhum elemento tem este valor.' : `${gs.length} elementos têm este valor: a chave não identifica um só.`,
        })
        continue
      }
      guid = gs[0]
    } else {
      guid = valorChave
      if (m.porGuid(guid) === null) {
        plano.semElemento.push({ linha: r + 1, chave: guid, motivo: 'Não há nenhum elemento com esta GlobalId.' })
        continue
      }
    }
    if (vistos.has(guid)) plano.repetidas.push(guid)
    vistos.add(guid)
    for (const c of colunas) {
      const v = (linha[c.coluna] ?? '').trim()
      if (v === '') continue
      plano.operacoes.push({
        tipo: 'definir',
        guid,
        pset: c.pset,
        propriedade: c.propriedade,
        valor: v,
        ...(c.tipoDeValor ? { tipoDeValor: c.tipoDeValor } : {}),
        ...(c.forma ? { forma: c.forma } : {}),
        ...(c.classeDaQuantidade ? { classeDaQuantidade: c.classeDaQuantidade } : {}),
      })
      registar(guid, `${c.pset}.${c.propriedade}`, ler(guid, c.pset, c.propriedade)?.valor ?? null, v)
    }
    if (ix) {
      const obj = m.porGuid(guid)!
      for (const c of associacoes) {
        const v = (linha[c.coluna] ?? '').trim()
        if (v === '') continue
        if (c.tipo === 'material') {
          const actual = materialTexto(materialDe(m, ix, obj))
          // A folha devolvida tal como saiu: nada a fazer.
          if (actual === v) continue
          plano.operacoes.push({ tipo: 'material', guid, material: v })
          registar(guid, 'Material', actual || null, v)
        } else {
          const { codigo, nome } = lerCodigoENome(v)
          plano.operacoes.push({ tipo: 'classificar', guid, sistema: c.sistema, codigo, ...(nome ? { nome } : {}) })
          const actual = classificacoesDe(m, ix, obj).find((x) => x.sistema === c.sistema)
          registar(guid, `Classificação: ${c.sistema}`, actual?.codigo ?? null, codigo)
        }
      }
    }
  }
  return plano
}

/** "Ss_25_10_30 | Paredes" -> código e nome; sem barra, só o código. */
export function lerCodigoENome(celula: string): { codigo: string; nome?: string } {
  const i = celula.indexOf('|')
  if (i < 0) return { codigo: celula.trim() }
  const nome = celula.slice(i + 1).trim()
  return { codigo: celula.slice(0, i).trim(), ...(nome ? { nome } : {}) }
}

/** O cabeçalho de uma coluna de propriedade, no formato que lerCabecalho lê. */
export function escreverCabecalho(c: CabecalhoDePropriedade): string {
  const anot =
    c.forma === 'quantidade' && c.classeDaQuantidade
      ? canonicalIfcName(c.classeDaQuantidade)
      : [c.forma === 'lista' || c.forma === 'intervalo' ? c.forma : '', c.tipoDeValor ? canonicalIfcName(c.tipoDeValor) : '']
          .filter(Boolean)
          .join(' ')
  return `${c.pset}.${c.propriedade}${anot ? ` [${anot}]` : ''}`
}

/** Elementos (IfcRoot com GlobalId) das classes pedidas. Sem classes,
 *  todos os que têm pelo menos um Pset de instância. */
function elementosPara(m: ModeloStep, classes: string[]): number[] {
  if (classes.length > 0) return classes.flatMap((c) => m.idsDaClasse(c.toUpperCase()))
  const out = new Set<number>()
  for (const rel of m.idsDaClasse('IFCRELDEFINESBYPROPERTIES')) for (const o of lerListaDeRefs(m.args(rel)[4])) out.add(o)
  return [...out].sort((a, b) => a - b)
}

/** A folha para preencher: GlobalId, Classe, Nome e uma coluna por
 *  propriedade pedida, com o valor que o ficheiro já tem. */
export function modeloDePreenchimento(
  m: ModeloStep,
  opcoes: { classes: string[]; colunas: CabecalhoDePropriedade[]; material?: boolean; sistemas?: string[] },
): Folha {
  const ler = criarLeitor(m)
  const unidadeDe = criarLeitorDeUnidades(m)
  const sistemas = opcoes.sistemas ?? []
  const ix = opcoes.material || sistemas.length > 0 ? construirIndiceDeAssociacoes(m) : null

  // Primeiro os valores, com a unidade de cada um: só depois se sabe se
  // uma coluna tem uma unidade só (vai para o cabeçalho) ou várias (vai
  // para uma coluna ao lado, linha a linha).
  const elementos: { guid: string; classe: string; nome: string; valores: { valor: Celula; unidade: string | null }[]; resto: Celula[] }[] = []
  for (const id of elementosPara(m, opcoes.classes)) {
    const a = m.args(id)
    const guid = lerArgTexto(a[0])
    if (!guid || guid.length !== 22) continue
    const classificacoes = ix && sistemas.length > 0 ? classificacoesDe(m, ix, id) : []
    elementos.push({
      guid,
      classe: canonicalIfcName(m.classe(id) ?? ''),
      nome: lerArgTexto(a[2]) ?? '',
      valores: opcoes.colunas.map((c) => {
        const v = ler(guid, c.pset, c.propriedade)
        if (!v || v.valor === null) return { valor: '', unidade: null }
        return { valor: celulaDoValor(v.valor, v.tipo), unidade: unidadeDe(v.id) }
      }),
      resto: [
        // Um material composto não se edita numa célula: vai entre parênteses
        // para se ver, e a importação recusa-o se vier igual.
        ...(opcoes.material && ix ? [materialTexto(materialDe(m, ix, id))] : []),
        ...sistemas.map((s) => {
          const c = classificacoes.find((x) => x.sistema === s)
          if (!c || c.codigo === null) return ''
          return c.nome ? `${c.codigo} | ${c.nome}` : c.codigo
        }),
      ],
    })
  }
  const unidadesDaColuna = opcoes.colunas.map(
    (_, i) => new Set(elementos.map((e) => e.valores[i].unidade).filter((u): u is string => u !== null)),
  )
  const cabecalho: Celula[] = ['GlobalId', 'Classe', 'Nome']
  opcoes.colunas.forEach((c, i) => {
    const us = unidadesDaColuna[i]
    if (us.size === 1) cabecalho.push(`${escreverCabecalho(c)} {${[...us][0]}}`)
    else if (us.size > 1) {
      cabecalho.push(`${escreverCabecalho(c)} {unidades várias}`)
      // Sem pontos no nome: uma coluna sem ponto é só para ler.
      cabecalho.push(`Unidade (${c.pset} ${c.propriedade})`.replace(/\./g, ' '))
    } else cabecalho.push(escreverCabecalho(c))
  })
  cabecalho.push(...(opcoes.material ? ['Material'] : []), ...sistemas.map((s) => `Classificação: ${s}`))

  const linhas: Celula[][] = [cabecalho]
  for (const e of elementos) {
    const linha: Celula[] = [e.guid, e.classe, e.nome]
    e.valores.forEach((v, i) => {
      linha.push(v.valor)
      if (unidadesDaColuna[i].size > 1) linha.push(v.unidade ?? '')
    })
    linha.push(...e.resto)
    linhas.push(linha)
  }
  return { nome: 'Preencher', linhas }
}

/** Um valor numérico vai para a folha como número (o Excel soma-o e
 *  ordena-o); o resto como texto. Listas e intervalos ficam texto. */
function celulaDoValor(valor: string, tipo: string | null): Celula {
  const t = (tipo ?? '').toUpperCase()
  const numerico = t.startsWith('IFCQUANTITY') || generoDoTipo(t) === 'numero' || generoDoTipo(t) === 'inteiro'
  if (!numerico) return valor
  const n = Number(valor)
  return Number.isFinite(n) && valor.trim() !== '' ? n : valor
}

function materialTexto(x: { texto: string | null; simples: boolean }): string {
  if (x.texto === null) return ''
  return x.simples ? x.texto : `(${x.texto})`
}
